<script>
	// NPM Modules
	import { createEventDispatcher } from 'svelte';
	// Stores
	import RaceStore from '@/Stores/Racing/RaceStore.js';
	import SelectionStore from '@/Stores/Racing/SelectionStore.js';
	import PriceStore from '@/Stores/Racing/PriceStore.js';
	import PreferenceStore from '@/Stores/PreferenceStore.js';
	// Partials
	import SelectionRow from '@/Partials/Racing/Selections/_SelectionRow.svelte';
	// Constants
	import { SelectionStatus } from '@/Constants/Selection.js';
	import { BetType } from '@/Constants/Bet.js';
	import { Products } from '@/Constants/RaceProducts.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	import { NumberFormat } from '@/Services/Helpers';
	import Prompt from '@/Components/Prompt.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';

	const dispatch = createEventDispatcher();

	export let selectedRaceId;

	$: includeMulti = $PreferenceStore.racing.racingIncludeMulti;
	$: activeSelectionIds =
		$SelectionStore && $SelectionStore.where('status.id', SelectionStatus.Active).pluck('id').toArray();
	$: favourite =
		$PriceStore &&
		$PriceStore
			.whereIn('selection_id', activeSelectionIds)
			.where('product.id', Products.Fixed)
			.where('bet_type.id', BetType.Win)
			.where('price', '>', 0)
			.sortBy('price')
			.first();
	$: ($PriceStore || previewDivisor || !previewDivisor || divisor) && calcPercent();
	$: (selectedRaceId || $RaceStore) && !previewDivisor && setRaceDivisor();
	$: race = selectedRaceId && $RaceStore && $RaceStore.firstWhere('id', selectedRaceId);

	let winPercent = 0;
	let placePercent = 0;
	let previewDivisor = false;
	let divisor = 0;
	let notValidDivisor = false;
	let promptScratchSelection = false;
	let promptUnscratchSelection = false;
	let promptUpdateSelection = false;
	let promptUnresultSelection = false;
	let promptUnresultSelectionBetType = 'skip';
	let selectionToUnresult;
	let selectionToScratch;
	let selectionToUnscratch;
	let selectionToUpdate;
	let sending = false;
	let selectionUpdate = {
		starting_price: null,
		top_fluc: null
	};

	function setRaceDivisor() {
		divisor = ($RaceStore && $RaceStore.where('id', selectedRaceId).pluck('fixed_place_divisor').first()) || 0;
	}

	function calcPercent() {
		winPercent = 0;
		placePercent = 0;
		$SelectionStore.where('status.id', SelectionStatus.Active).each((selection) => {
			winPercent += calcPercentForBetType(selection.id, BetType.Win);
			placePercent += calcPercentForBetType(selection.id, BetType.Place);
		});
	}

	function calcPercentForBetType(selectionId, betType) {
		if (previewDivisor && betType === BetType.Place) {
			let price = PriceStore.calcDivisorPlacePrice(selectionId, divisor);
			return price ? 100 / price : 0;
		} else {
			let product = $PriceStore
				.where('selection_id', selectionId)
				.where('product.id', Products.Fixed)
				.firstWhere('bet_type.id', betType);

			return product ? 100 / (product.price / 100) : 0;
		}
	}

	function calcHold(betType, formatOutput) {
		let hold = (race && race.liabilities && race.liabilities[betType]) || 0;
		return hold ? NumberFormat(hold, false, false) : formatOutput ? '-' : hold;
	}

	function scratchSelectionClicked() {
		promptScratchSelection = true;
	}

	function scratchSelectionClosed() {
		promptScratchSelection = false;
		selectionToScratch = null;
	}

	function scratchSelection() {
		sending = true;
		selectionToScratch
			.scratch()
			.then(() => {
				notify({
					type: 'success',
					title: 'Selection successfully scratched',
					message: `${selectionToScratch.number}. ${selectionToScratch.name}, has been successfully scratched!`,
					timeout: 3
				});
				scratchSelectionClosed();
				dispatch('refresh_race');
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Problem scratching selection',
					message: e.error[0] || 'There was a problem scratching this selection',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}

	function unscratchSelectionClicked() {
		promptUnscratchSelection = true;
	}

	function unscratchSelectionClosed() {
		promptUnscratchSelection = false;
		selectionToUnscratch = null;
	}

	function unscratchSelection() {
		sending = true;
		selectionToUnscratch
			.unscratch()
			.then(() => {
				notify({
					type: 'success',
					title: 'Selection successfully unscratched',
					message: `${selectionToUnscratch.number}. ${selectionToUnscratch.name}, has been successfully unscratched!`,
					timeout: 3
				});
				unscratchSelectionClosed();
				dispatch('refresh_race');
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Problem unscratching selection',
					message: e.error[0] || 'There was a problem unscratching this selection',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}

	function updateSelectionClicked() {
		selectionUpdate = {
			starting_price: selectionToUpdate.starting_price,
			top_fluc: selectionToUpdate.top_fluc
		};
		promptUpdateSelection = true;
	}

	function updateSelectionClosed() {
		promptUpdateSelection = false;
		selectionToUpdate = null;
		selectionUpdate = {
			starting_price: null,
			top_fluc: null
		};
	}

	function updateSelection() {
		sending = true;
		selectionToUpdate
			.updateSelection(selectionUpdate)
			.then(() => {
				notify({
					type: 'success',
					title: 'Selection successfully updated',
					message: `${selectionToUpdate.number}. ${selectionToUpdate.name}, has been successfully updated!`,
					timeout: 3
				});
				updateSelectionClosed();
				dispatch('refresh_race');
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Problem updating selection',
					message: e.error[0] || 'There was a problem updating this selection',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}

	function unresultSelectionClicked() {
		promptUnresultSelection = true;
	}

	function unresultSelection(selection) {
		if (!promptUnresultSelectionBetType) {
			return false;
		}
		let betTypes = null;
		if (promptUnresultSelectionBetType == 'both') {
			betTypes = [BetType.Win, BetType.Place];
		} else if (promptUnresultSelectionBetType == 'win') {
			betTypes = [BetType.Win];
		} else if (promptUnresultSelectionBetType == 'place') {
			betTypes = [BetType.Place];
		}

		selection
			.unresult(betTypes ? { bet_type_ids: betTypes } : null)
			.then(() => {
				notify({
					type: 'success',
					title: selection.name,
					message: 'Selection successfully unresulted',
					timeout: 3
				});
				dispatch('refresh_race');
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to unresult selection',
					timeout: 5
				});
			});
	}
</script>

{#if promptScratchSelection}
	<Modal on:close={scratchSelectionClosed} className="md:w-1/2 lg:w-1/4 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center uppercase">Scratch selection</h2>
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<p class="mb-4 text-center">
				Are you sure you want to scratch this selection?<br /><br />
				<span class="font-bold">{selectionToScratch.number}. {selectionToScratch.name}</span>
			</p>
			<div class="flex flex-row justify-around">
				<button class="w-32 mb-4 btn btn-muted" on:click={scratchSelectionClosed}> Cancel</button>
				<LoadingButton loading={sending} className="w-32 mb-4 btn btn-primary" on:click={scratchSelection}>
					Yes
				</LoadingButton>
			</div>
		</div>
	</Modal>
{/if}

{#if promptUnscratchSelection}
	<Modal on:close={unscratchSelectionClosed} className="md:w-1/2 lg:w-1/4 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center uppercase">Unscratch selection</h2>
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			<p class="mb-4 text-center">
				Are you sure you want to unscratch this selection?<br /><br />
				<span class="font-bold">{selectionToUnscratch.number}. {selectionToUnscratch.name}</span>
			</p>
			<div class="flex flex-row justify-around">
				<button class="w-32 mb-4 btn btn-muted" on:click={unscratchSelectionClosed}> Cancel</button>
				<LoadingButton loading={sending} className="w-32 my-4 btn btn-warning" on:click={unscratchSelection}>
					Yes
				</LoadingButton>
			</div>
		</div>
	</Modal>
{/if}

{#if promptUnresultSelection}
	<Prompt
		bind:open={promptUnresultSelection}
		title="Confirm unresulting selection"
		onConfirm={() => unresultSelection(selectionToUnresult)}
	>
		<div class="m-3">
			<h3 class="font-semibold">{selectionToUnresult.number}. {selectionToUnresult.name}</h3>
			<br />
			<span class="text-xl">
				This will un-result all the bets already placed and cancel withdrawals, only do this when you have received
				incorrect results
			</span>
			<br />
			<br />
			<div class="flex flex-row justify-center items-center">
				<SelectInput
					label="Bet types"
					className="py-1 w-1/3"
					name="sport"
					required={true}
					bind:value={promptUnresultSelectionBetType}
				>
					<option value={'skip'}>All bet types</option>
					<option value={'both'}>Both Win and Place bets</option>
					<option value={'win'}>Win bets</option>
					<option value={'place'}>Place bets</option>
				</SelectInput>
			</div>
			<br />
			<span class="font-semibold text-xl">Are you sure you want to unfinalise the selection?</span>
		</div>
	</Prompt>
{/if}

{#if promptUpdateSelection}
	<Modal on:close={updateSelectionClosed} className="md:w-1/2 lg:w-1/4 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center uppercase">Update selection</h2>
		<div class="flex flex-col w-full px-4 pt-2 space-y-4">
			<p class="mb-2 mt-1 text-center">
				<span class="font-bold">{selectionToUpdate.number}. {selectionToUpdate.name}</span>
			</p>
			<div>
				<label class="form-label" for="selection_starting_price">Starting Price ($)</label>
				<input
					type="number"
					id="selection_starting_price"
					name="selection_starting_price"
					class="px-2 form form-input focus:outline-none"
					value={selectionUpdate.starting_price / 100 || null}
					step="0.01"
					required
					on:change={(e) => (selectionUpdate.starting_price = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
				/>
			</div>
			<div>
				<label class="form-label" for="selection_top_fluc">Top Fluc ($)</label>
				<input
					type="number"
					id="selection_top_fluc"
					name="selection_top_fluc"
					class="px-2 form form-input focus:outline-none"
					value={selectionUpdate.top_fluc / 100 || null}
					step="0.01"
					required
					on:change={(e) => (selectionUpdate.top_fluc = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
				/>
			</div>
			<div class="flex flex-row justify-around">
				<button class="w-32 mb-4 btn btn-muted" on:click={updateSelectionClosed}> Cancel</button>
				<LoadingButton loading={sending} className="w-32 mb-4 btn btn-primary" on:click={updateSelection}>
					Update
				</LoadingButton>
			</div>
		</div>
	</Modal>
{/if}

<div class="divide-y">
	<ul class="grid racing-selection-grid pb-1 font-bold uppercase">
		<li class="flex text-center">
			<div class="w-10">NSW</div>
			<div class="w-10 mx-1">VIC</div>
			<div class="w-10">QLD</div>
		</li>
		<li class="text-center">Flucs</li>
		<li class="flex text-center">
			<div class="flex">
				<div class="w-12 mr-1">WIN ROLL</div>
				<div class="flex flex-col w-12 mr-1">
					<div>WIN</div>
					<div class="text-xs">{NumberFormat(winPercent.toFixed(0), true)}%</div>
				</div>
				<div class="w-12 mr-1">PLC ROLL</div>
				<div class="flex flex-col w-12 mr-1">
					<!--
			  // Note: 9th Jul 2021 (Jira WG-1412)
			  // Turn off Divisor. Left all code if we turn back on.
			  <Divisor
			  bind:divisor
			  bind:previewDivisor
			  bind:notValidDivisor
			  {selectedRaceId}
			  {placePercent} /> -->
					PLC
					<div class="text-xs {notValidDivisor ? 'text-red-500' : ''}">
						{NumberFormat(placePercent && placePercent.toFixed(0), true)}%
					</div>
				</div>
			</div>
		</li>
		<li>Selection</li>
		<li class="w-px" />
		<li class="text-center">Win Result</li>
		<li class="text-center">C/O Result</li>
		<li class="text-center">Win Take</li>
		<li class="text-center">Plc Take</li>
		<li class="text-center">Avg Div</li>
		<li class="text-center">Win Hold</li>
		<li class="text-center">Plc Hold</li>
		<li class="text-center">C/O Hold</li>
	</ul>
	{#if $SelectionStore.count()}
		{#each $SelectionStore
			.sortBy('number')
			.where('status.id', SelectionStatus.Active)
			.all() as selection (selection.id)}
			<SelectionRow
				{selection}
				{favourite}
				{includeMulti}
				{previewDivisor}
				{divisor}
				{notValidDivisor}
				{race}
				bind:selectionToScratch
				bind:selectionToUpdate
				bind:selectionToUnresult
				on:scratch={scratchSelectionClicked}
				on:updateSelection={updateSelectionClicked}
				on:unresultSelection={unresultSelectionClicked}
			/>
		{/each}
		{#each $SelectionStore
			.sortBy('number')
			.where('status.id', '!==', SelectionStatus.Active)
			.all() as selection (selection.id)}
			<SelectionRow
				{selection}
				{favourite}
				{includeMulti}
				{previewDivisor}
				{divisor}
				{notValidDivisor}
				{race}
				bind:selectionToUnscratch
				on:unscratch={unscratchSelectionClicked}
			/>
		{/each}
		<ul class="grid racing-selection-grid py-2 font-bold uppercase">
			<li class="col-span-10" />
			<li class="text-center">{race && calcHold('win_hold')}</li>
			<li class="text-center">{race && calcHold('place_hold')}</li>
			<li class="text-center">
				{NumberFormat(
					race && includeMulti
						? (
								parseFloat(calcHold('cashout_hold', false).toString()) +
								parseFloat(calcHold('cashout_multi_hold', false).toString())
							).toFixed(2)
						: parseFloat(calcHold('cashout_hold', false).toString()).toFixed(2),
					true,
					false
				)}
			</li>
		</ul>
	{:else}
		<div class="p-4 text-center">Loading...</div>
	{/if}
</div>
<!-- TABLE KEY -->
<div class="flex justify-end mt-2 text-sm">
	<div class="flex">
		<div class="ml-1 -mt-1 text-base font-bold text-blue-500">*</div>
		<div class="italic">Has Multi Bet</div>
	</div>
	<div class="flex">
		<div class="ml-1 -mt-1 font-bold text-red-500" title="Favourite">*</div>
		<div class="italic">Favourite</div>
	</div>
</div>

<style>
	.racing-selection-grid {
		grid-template-columns: 1fr 1.2fr 1.5fr 1.5fr 0.3fr repeat(8, minmax(0, 0.85fr));
	}
</style>
